import React from "react";
import { Link as LinkIcon } from "lucide-react";

export default function Hero({ seo }) {
  const h1 = seo?.h1 || "Deep-Chain Forensics for High-Asset Litigation.";
  const h2 = seo?.h2 || "Expert technical recovery and asset tracing for law firms and private investigators.";

  return (
    <section
      className="relative min-h-screen w-full overflow-hidden border-b border-slate-800/80"
      data-testid="hero-section"
    >
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg opacity-80 pointer-events-none" />
      {/* Subtle scanline texture for edge */}
      <div className="absolute inset-0 scanlines pointer-events-none" />
      {/* Radial vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 20%, rgba(56,189,248,0.06) 0%, rgba(6,10,17,0.0) 45%), radial-gradient(ellipse at 30% 80%, rgba(56,189,248,0.04) 0%, rgba(6,10,17,0.0) 50%)",
        }}
      />

      {/* Top rail */}
      <div className="relative z-10 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="brand-lockup">
            <div className="w-6 h-6 border border-white/70 relative">
              <div className="absolute inset-1 bg-white/90" />
            </div>
            <span className="mono-label text-white/90">SAUDERTON · FORENSICS</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="mono-label hover:text-white transition-colors" data-testid="nav-services">Services</a>
            <a href="#methodology" className="mono-label hover:text-white transition-colors" data-testid="nav-methodology">The Process</a>
            <a href="#about" className="mono-label hover:text-white transition-colors" data-testid="nav-about">About</a>
            <a href="#inquiry" className="mono-label hover:text-white transition-colors" data-testid="nav-inquiry">Inquire</a>
            <a href="/media" className="mono-label hover:text-white transition-colors" data-testid="nav-media">Media Contact</a>
          </div>
          <a href="#inquiry" className="hidden md:inline-flex btn-secondary" data-testid="nav-cta">
            Start an Inquiry
          </a>
        </div>
      </div>

      {/* Voice tagline strip — direct first-person promise */}
      <div
        className="relative z-10 border-b border-slate-800/60 bg-[#101A2F]"
        data-testid="hero-tagline-strip"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-4 sm:gap-6 flex-wrap">
          <span className="mono-label text-slate-500 shrink-0">VOICE · PRINCIPAL</span>
          <span className="hidden sm:inline-block w-8 h-px bg-slate-700 shrink-0" />
          <p className="font-serif-display italic text-white text-base sm:text-lg leading-tight flex-1 min-w-0">
            Where did the Bitcoin go?
            <span className="text-slate-400 not-italic font-mono text-sm sm:text-base"> — </span>
            Let me take a look, and I will show you exactly where it went.<span className="caret-blink not-italic text-bronze ml-1">▍</span>
          </p>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-24">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8 fade-up">
            <div className="flex items-center gap-3 mb-10">
              <span className="inline-block w-8 h-px bg-slate-600" />
              <span className="mono-label">TECHNICAL CONSULTANT · DIGITAL ASSET TRACING FOR LITIGATION</span>
            </div>

            <h1
              className="font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight relative pb-6"
              data-testid="hero-h1"
            >
              {h1}
              <span className="absolute left-0 bottom-0 w-24 h-px bg-bronze" />
            </h1>

            <p
              className="mt-10 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed"
              data-testid="hero-h2"
            >
              Expert technical recovery and digital asset tracing for law firms, corporations and private investigators.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a href="#inquiry" className="btn-primary" data-testid="hero-cta-inquiry">
                Start a Feasibility Inquiry
              </a>
              <a href="#services" className="btn-secondary" data-testid="hero-cta-services">
                Fixed Fee Services
              </a>
            </div>

            <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 border-t border-slate-800/70">
              {[
                ["20+ YR", "FULL-STACK + BLOCKCHAIN"],
                ["$420M+", "ASSETS MAPPED"],
                ["48 HR", "FEASIBILITY TURNAROUND"],
                ["LEGACY BTC", "2010 — 2020 INFRASTRUCTURE"],
              ].map(([v, k]) => (
                <div
                  key={k}
                  className="border-r last:border-r-0 border-slate-800/70 py-6 pr-6"
                  data-testid={`stat-${k.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="font-serif-display text-2xl sm:text-3xl text-white">{v}</div>
                  <div className="mono-label mt-2">{k}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: schematic panel */}
          <div className="col-span-12 lg:col-span-4 hidden lg:block">
            <div className="relative bg-[#152138]/70 backdrop-blur border border-slate-800/80 p-8 fade-up corner-brackets" style={{ animationDelay: "0.2s" }}>
              <span className="corner-tl" />
              <span className="corner-br" />
              <div className="mono-label mb-6 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-rose-500 rounded-full node-pulse" />
                CASE LEDGER · LIVE
              </div>
              <div className="space-y-5 font-mono text-xs text-slate-300">
                {[
                  { id: "CL-2847", status: "TRACED", addr: "bc1q…n7u4", val: "$2.4M" },
                  { id: "CL-2846", status: "RECOVERED", addr: "1F1ta…8vZ", val: "$18.7M" },
                  { id: "CL-2845", status: "FEASIBLE", addr: "3J98t…kq2", val: "$640K" },
                  { id: "CL-2844", status: "IN TRACE", addr: "bc1qx…e8g", val: "$3.1M" },
                ].map((r) => (
                  <div key={r.id} className="flex items-center justify-between border-b border-slate-800/60 pb-3">
                    <div className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 bg-sky-400 node-pulse rounded-full" />
                      <span className="text-slate-500">{r.id}</span>
                      <span className="text-slate-400">{r.addr}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-white">{r.val}</div>
                      <div className="mono-label text-[9px]">{r.status}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex items-center gap-2 mono-label">
                <LinkIcon className="w-3 h-3" />
                INDEX · BTC / ETH / TRX / LTC
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom hairline */}
      <div className="relative z-10 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <span className="mono-label">SCROLL · SERVICES</span>
          <span className="mono-label">01 / 05</span>
        </div>
      </div>
    </section>
  );
}
